import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Grid,
  Alert,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { incomeAPI, referenceAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import { cardSx } from '../theme';
import { SummaryTotalCard, SummaryTile, SummaryStripSkeleton, summaryStripSx, summaryPanelMinHeight } from '../components/CategorySummary';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddIncomeTypeModal from '../components/AddIncomeTypeModal';
import AddIncomeLocationModal from '../components/AddIncomeLocationModal';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const normalizeId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Both reference lists are reloaded from two places each — the page load and the
// "add new" modal's onSuccess — so the ordering lives here rather than in four copies.
const sortIncomeTypes = (payload) => [...toArray(payload, 'income types')].sort((a, b) => {
  if (a.sort_order !== b.sort_order) {
    return (a.sort_order || 0) - (b.sort_order || 0);
  }
  if (a.category !== b.category) {
    return (a.category || '').localeCompare(b.category || '');
  }
  return (a.name || '').localeCompare(b.name || '');
});

const sortIncomeLocations = (payload) => [...toArray(payload, 'income locations')].sort((a, b) => {
  if (a.sort_order !== b.sort_order) {
    return (a.sort_order || 0) - (b.sort_order || 0);
  }
  if (a.name !== b.name) {
    return (a.name || '').localeCompare(b.name || '');
  }
  return (a.location || '').localeCompare(b.location || '');
});

const IncomesPage = () => {
  const notify = useNotify();
  const currentYear = new Date().getFullYear();
  const [income, setIncome] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [incomeLocations, setIncomeLocations] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('income_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modals for adding reference data
  const [openIncomeTypeModal, setOpenIncomeTypeModal] = useState(false);
  const [openIncomeLocationModal, setOpenIncomeLocationModal] = useState(false);

  const [formData, setFormData] = useState({
    income_date: '',
    income_time: '',
    income_type_id: '',
    income_location_id: '',
    amount: '',
    summary: '',
    notes: '',
  });

  const yearOptions = useMemo(() => (
    Array.from({ length: 11 }, (_, index) => String(currentYear - index))
  ), [currentYear]);

  const incomeLocationOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...incomeLocations.map((loc) => ({
      id: loc.id,
      label: `${loc.name}${loc.location ? ` - ${loc.location}` : ''}${loc.online ? ' (Online)' : ''}`,
    })),
  ], [incomeLocations]);

  const loadData = useCallback(async () => {
    setLoadError('');
    try {
      const listParams = {};
      const summaryParams = {};

      if (selectedYear) {
        listParams.start_date = `${selectedYear}-01-01`;
        listParams.end_date = `${selectedYear}-12-31`;
        summaryParams.year = selectedYear;
      }

      // allSettled rather than all: four independent endpoints, and one of them dying
      // should cost its own list rather than discarding the three that answered.
      const results = await Promise.allSettled([
        incomeAPI.getAll(listParams),
        incomeAPI.getSummary(summaryParams),
        referenceAPI.getIncomeTypes(),
        referenceAPI.getIncomeLocations(),
      ]);
      const [incomeRes, summaryRes, typesRes, locationsRes] = results;

      // Every payload is normalized before it reaches state. A stored non-array does not
      // fail the fetch — it throws later, inside the render that spreads and sorts it,
      // and an exception there unmounts the whole app instead of one list.
      if (incomeRes.status === 'fulfilled') {
        setIncome(toArray(incomeRes.value?.data, 'income'));
      }
      if (summaryRes.status === 'fulfilled') {
        setSummary(toArray(summaryRes.value?.data, 'income summary'));
      }
      if (typesRes.status === 'fulfilled') {
        setIncomeTypes(sortIncomeTypes(typesRes.value?.data));
      }
      if (locationsRes.status === 'fulfilled') {
        setIncomeLocations(sortIncomeLocations(locationsRes.value?.data));
      }

      const failure = results.find((result) => result.status === 'rejected');
      if (failure) {
        console.error('Error loading data:', failure.reason);
        // Whatever loaded is left on screen on purpose: a failed refresh should say so
        // rather than blank the page out into something indistinguishable from "no income".
        setLoadError(
          failure.reason?.response?.data?.error || 'Unable to load income records. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError(error?.response?.data?.error || 'Unable to load income records. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useRefreshOnFocus(loadData);

  const incomeTypeMap = useMemo(() => {
    const map = new Map();
    incomeTypes.forEach((type) => {
      if (type?.id !== undefined && type?.id !== null) {
        map.set(type.id, type);
      }
    });
    return map;
  }, [incomeTypes]);

  const incomeLocationMap = useMemo(() => {
    const map = new Map();
    incomeLocations.forEach((loc) => {
      if (loc?.id !== undefined && loc?.id !== null) {
        map.set(loc.id, loc);
      }
    });
    return map;
  }, [incomeLocations]);

  const handleOpenDialog = (incomeRecord = null) => {
    if (incomeRecord) {
      setEditingIncome(incomeRecord);
      // Format time for input (HH:MM)
      let timeValue = '';
      if (incomeRecord.income_time) {
        const timeStr = incomeRecord.income_time;
        if (timeStr.includes(':')) {
          const parts = timeStr.split(':');
          timeValue = `${parts[0]}:${parts[1]}`;
        }
      }
      setFormData({
        income_date: incomeRecord.income_date || '',
        income_time: timeValue,
        income_type_id: incomeRecord.income_type_id || '',
        income_location_id: incomeRecord.income_location_id || '',
        amount: incomeRecord.amount || '',
        summary: incomeRecord.summary || '',
        notes: incomeRecord.notes || '',
      });
    } else {
      setEditingIncome(null);
      setFormData({
        income_date: '',
        income_time: '',
        income_type_id: '',
        income_location_id: '',
        amount: '',
        summary: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIncome(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const response = editingIncome
        ? await incomeAPI.update(editingIncome.id, formData)
        : await incomeAPI.create(formData);
      handleCloseDialog();
      // The Year filter can hide a record whose date falls outside it — switch
      // to the saved record's year instead of just reloading under the old one.
      const savedYear = response?.data?.income_date?.slice(0, 4);
      if (savedYear && savedYear !== selectedYear) {
        setSelectedYear(savedYear);
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Error saving income:', error);
      notify.error(errorMessage(error, 'Error saving income'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await incomeAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting income:', error);
      notify.error(errorMessage(error, 'Error deleting income'));
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0.00';
    }
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getIncomeCategoryDisplay = useCallback((incomeRecord) => {
    if (!incomeRecord) return '-';
    const typeId = normalizeId(incomeRecord.income_type_id);
    const type = typeId !== null ? incomeTypeMap.get(typeId) : null;
    return type?.category || incomeRecord.income_type || '-';
  }, [incomeTypeMap]);

  const getIncomeLocationDisplay = useCallback((incomeRecord) => {
    if (!incomeRecord) return '-';
    const locationId = normalizeId(incomeRecord.income_location_id);
    const location = locationId !== null ? incomeLocationMap.get(locationId) : null;
    if (location) {
      let label = location.name || '';
      if (location.location) {
        label += `${label ? ' - ' : ''}${location.location}`;
      }
      if (location.online) {
        label += `${label ? ' ' : ''}(Online)`;
      }
      return label || '-';
    }
    return incomeRecord.income_location || '-';
  }, [incomeLocationMap]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortedIncome = useMemo(() => {
    const getSortValue = (incomeRecord) => {
      switch (sortBy) {
        case 'category':
          return getIncomeCategoryDisplay(incomeRecord);
        case 'income_location':
          return getIncomeLocationDisplay(incomeRecord);
        case 'income_time':
          return incomeRecord.income_time || '';
        case 'amount':
          return incomeRecord.amount || 0;
        case 'summary':
          return incomeRecord.summary || '';
        case 'notes':
          return incomeRecord.notes || '';
        case 'income_date':
        default:
          return incomeRecord.income_date || '';
      }
    };

    const sorted = [...income].sort((a, b) => {
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      const result = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return sortOrder === 'asc' ? result : -result;
    });
    return sorted;
  }, [income, sortBy, sortOrder, getIncomeCategoryDisplay, getIncomeLocationDisplay]);

  const handleCategoryClick = (categoryName) => {
    if (categoryName === selectedCategory) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryName);
    }
  };

  const getCategoryBreakdown = useMemo(() => {
    if (!selectedCategory) return [];

    // Filter income by selected category
    const filteredIncome = income.filter((incomeRecord) => {
      // Check category_name if available
      if (incomeRecord.category_name === selectedCategory) {
        return true;
      }
      // Check via income type's category
      const typeId = normalizeId(incomeRecord.income_type_id);
      if (typeId !== null) {
        const type = incomeTypeMap.get(typeId);
        if (type && type.category === selectedCategory) {
          return true;
        }
      }
      return false;
    });

    // Group by income_type_id and sum amounts
    const grouped = new Map();
    filteredIncome.forEach((incomeRecord) => {
      const typeId = normalizeId(incomeRecord.income_type_id);
      if (typeId !== null) {
        const type = incomeTypeMap.get(typeId);
        if (type) {
          const key = typeId;
          if (!grouped.has(key)) {
            grouped.set(key, {
              incomeTypeId: typeId,
              incomeTypeName: type.name, // Just the name, not "category: name"
              total: 0,
            });
          }
          const item = grouped.get(key);
          item.total += incomeRecord.amount || 0;
        }
      }
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.incomeTypeName.localeCompare(b.incomeTypeName)
    );
  }, [income, selectedCategory, incomeTypeMap]);

  const sortedSummary = useMemo(() => (
    [...summary].sort((a, b) => (b.total || 0) - (a.total || 0)
      || String(a.category || '').localeCompare(String(b.category || '')))
  ), [summary]);

  // Proportion bars are sized against the biggest tile in their own strip, so a
  // strip of small numbers still reads as a comparison instead of five stubs.
  const maxCategoryTotal = useMemo(
    () => sortedSummary.reduce((max, item) => Math.max(max, item.total || 0), 0),
    [sortedSummary],
  );
  const maxBreakdownTotal = useMemo(
    () => getCategoryBreakdown.reduce((max, item) => Math.max(max, item.total || 0), 0),
    [getCategoryBreakdown],
  );

  const totalIncome = income.reduce((sum, incomeRecord) => sum + (incomeRecord.amount || 0), 0);


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pt: 0.5, gap: 1 }}>
          <Typography variant="h4">Income</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              id="income-year-select"
              size="small"
              disableClearable
              sx={{ minWidth: { xs: 110, sm: 160 } }}
              options={['', ...yearOptions]}
              getOptionLabel={(year) => year || 'All Years'}
              filterOptions={createPrefixFilter((year) => year || 'All Years')}
              value={selectedYear}
              onChange={(event, newValue) => setSelectedYear(newValue || '')}
              renderInput={(params) => <TextField {...params} label="Year" />}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleOpenDialog()}
              sx={{ minWidth: { xs: 'auto' }, px: { xs: 0.5, sm: 2 }, height: 40 }}
            >
              <AddIcon fontSize="small" sx={{ mr: { xs: 0, sm: 0.75 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Add Income
              </Box>
            </Button>
          </Box>
        </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3} lg={2.5}>
          <SummaryTotalCard label="Total Income" value={totalIncome} formatCurrency={formatCurrency} loading={loading} />
        </Grid>
        <Grid item xs={12} md={9} lg={9.5}>
          <Paper sx={{ ...cardSx, p: { xs: 1.25, md: 2 }, minHeight: summaryPanelMinHeight }}>
            {loading ? (
              <SummaryStripSkeleton />
            ) : sortedSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No category data available.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {/* Category totals — tap one to drill into its income types */}
                <Box sx={summaryStripSx}>
                  {sortedSummary.map((item, index) => (
                    <SummaryTile
                      key={`${item.category}-${index}`}
                      label={item.category}
                      amount={item.total}
                      share={maxCategoryTotal ? ((item.total || 0) / maxCategoryTotal) * 100 : 0}
                      selected={selectedCategory === item.category}
                      onClick={() => handleCategoryClick(item.category)}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </Box>

                {/* Breakdown — only rendered when a category is selected */}
                {selectedCategory && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'text.secondary',
                        }}
                      >
                        {selectedCategory} breakdown
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedCategory(null)}
                        // Icon-only, so the name has to name the thing it
                        // closes rather than just saying "close".
                        aria-label={`Clear ${selectedCategory} breakdown`}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {getCategoryBreakdown.length > 0 ? (
                      <Box sx={summaryStripSx}>
                        {getCategoryBreakdown.map((item) => (
                          <SummaryTile
                            key={item.incomeTypeId}
                            label={item.incomeTypeName}
                            amount={item.total}
                            share={maxBreakdownTotal
                              ? ((item.total || 0) / maxBreakdownTotal) * 100
                              : 0}
                            formatCurrency={formatCurrency}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No income types recorded under {selectedCategory}.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell sx={{ pl: 1 }}>
                <TableSortLabel
                  active={sortBy === 'category'}
                  direction={sortBy === 'category' ? sortOrder : 'asc'}
                  onClick={() => handleSort('category')}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <TableSortLabel
                  active={sortBy === 'income_location'}
                  direction={sortBy === 'income_location' ? sortOrder : 'asc'}
                  onClick={() => handleSort('income_location')}
                >
                  Income Location
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'income_date'}
                  direction={sortBy === 'income_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('income_date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <TableSortLabel
                  active={sortBy === 'income_time'}
                  direction={sortBy === 'income_time' ? sortOrder : 'asc'}
                  onClick={() => handleSort('income_time')}
                >
                  Time
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'amount'}
                  direction={sortBy === 'amount' ? sortOrder : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <TableSortLabel
                  active={sortBy === 'summary'}
                  direction={sortBy === 'summary' ? sortOrder : 'asc'}
                  onClick={() => handleSort('summary')}
                >
                  Summary
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <TableSortLabel
                  active={sortBy === 'notes'}
                  direction={sortBy === 'notes' ? sortOrder : 'asc'}
                  onClick={() => handleSort('notes')}
                >
                  Notes
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={8} />}
            {sortedIncome.map((incomeRecord) => (
              <TableRow key={incomeRecord.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(incomeRecord) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(incomeRecord.id), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell sx={{ pl: 1 }}>{getIncomeCategoryDisplay(incomeRecord)}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{getIncomeLocationDisplay(incomeRecord)}</TableCell>
                <TableCell>{formatDate(incomeRecord.income_date) ?? '-'}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatTime(incomeRecord.income_time)}</TableCell>
                <TableCell align="right">${formatCurrency(incomeRecord.amount)}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{incomeRecord.summary || '-'}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{incomeRecord.notes || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIncome ? 'Edit Income' : 'Add New Income'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={incomeTypes}
                getOptionLabel={(type) => `${type.category}: ${type.name}`}
                isOptionEqualToValue={(type, value) => type.id === value?.id}
                filterOptions={createPrefixFilter((type) => `${type.category}: ${type.name}`)}
                componentsProps={{
                  popper: { disablePortal: true },
                  paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                }}
                value={incomeTypes.find((type) => type.id === formData.income_type_id) ?? null}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, income_type_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Income Type" required />}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOpenIncomeTypeModal(true)}
                sx={{ minWidth: 'auto', px: 0.5, height: 40 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={incomeLocationOptions}
                getOptionLabel={(loc) => loc.label}
                isOptionEqualToValue={(loc, value) => loc.id === value?.id}
                filterOptions={createPrefixFilter((loc) => loc.label)}
                componentsProps={{
                  popper: { disablePortal: true },
                  paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                }}
                value={incomeLocationOptions.find((loc) => loc.id === formData.income_location_id) ?? incomeLocationOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, income_location_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Income Location" />}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOpenIncomeLocationModal(true)}
                sx={{ minWidth: 'auto', px: 0.5, height: 40 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="income_date"
                  label="Date"
                  type="date"
                  value={formData.income_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="income_time"
                  label="Time"
                  type="time"
                  value={formData.income_time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ step: 60 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="amount"
                  label="Amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
            </Grid>
            <TextField
              size="small"
              name="summary"
              label="Summary"
              value={formData.summary}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              size="small"
              name="notes"
              label="Notes"
              value={formData.notes}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingIncome ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals for adding reference data */}
      <AddIncomeTypeModal
        open={openIncomeTypeModal}
        onClose={() => setOpenIncomeTypeModal(false)}
        apiCall={referenceAPI.createIncomeType}
        onSuccess={async (newType) => {
          // The type was created either way; a failed reload must not become an
          // unhandled rejection, and must not stop the new type being selected.
          try {
            const typesRes = await referenceAPI.getIncomeTypes();
            setIncomeTypes(sortIncomeTypes(typesRes?.data));
          } catch (error) {
            console.error('Error reloading income types:', error);
          }
          // Automatically select the newly created income type if we have its ID
          if (newType && newType.id) {
            setFormData(prev => ({ ...prev, income_type_id: newType.id }));
          }
        }}
      />

      <AddIncomeLocationModal
        open={openIncomeLocationModal}
        onClose={() => setOpenIncomeLocationModal(false)}
        apiCall={referenceAPI.createIncomeLocation}
        onSuccess={async (newLocation) => {
          try {
            const locationsRes = await referenceAPI.getIncomeLocations();
            setIncomeLocations(sortIncomeLocations(locationsRes?.data));
          } catch (error) {
            console.error('Error reloading income locations:', error);
          }
          // Automatically select the newly created location if we have its ID
          if (newLocation && newLocation.id) {
            setFormData(prev => ({ ...prev, income_location_id: newLocation.id }));
          }
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Income Record"
        message="Permanently delete this income record? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default IncomesPage;

